import { type ComponentProps, type CSSProperties, useContext } from 'react'
import { clsx } from 'clsx'
import { AnimatePresence, motion, type MotionProps, type Transition } from 'motion/react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Slot } from '@radix-ui/react-slot'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import { type RecipeVariants } from '@vanilla-extract/recipes'
import { CrossIcon } from '../../icons/CrossIcon'
import { xStack } from '../../patterns/stack'
import { framerTransitions } from '../../theme/transitions'
import { IconButton } from '../Button/IconButton'
import { Typography } from '../Typography/Typography'
import { Dialog } from './Dialog'
import { tokens } from '../../theme/tokens.css'
import {
  body,
  bodyScrollable,
  closeButton,
  content,
  contentMaxWidthVar,
  contentPaddingStyle,
  overlay,
  overlayFrosted,
} from './BaseDialog.css'

type RootProps = ComponentProps<typeof Dialog.Root>

function Root({ ...props }: RootProps) {
  return <Dialog.Root closingDuration={framerTransitions.defaultDuration * 1000} {...props} />
}

type OverlayProps = ComponentProps<typeof DialogPrimitive.Overlay> & {
  frosted?: boolean
}

function Overlay({ children, frosted = false, className, ...props }: OverlayProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={clsx(overlay, frosted && overlayFrosted, className)} {...props}>
        {children}
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  )
}

type ContentSize = NonNullable<RecipeVariants<typeof content>>['size']
type ContentVariant = NonNullable<RecipeVariants<typeof content>>['variant']
type ContentMobileLayout = NonNullable<RecipeVariants<typeof content>>['mobileLayout']

// Framer Motion overrides certain React HTML event handlers (e.g. onDrag, onAnimationStart) with
// incompatible signatures. Omitting conflicting keys prevents TypeScript from seeing the overloads.
type ArticleWithoutMotionConflicts = Omit<ComponentProps<'article'>, keyof MotionProps>

type ContentProps = ArticleWithoutMotionConflicts &
  MotionProps & {
    asChild?: never
    variant?: ContentVariant
    size?: ContentSize
    mobileLayout?: ContentMobileLayout
    /** Overrides the preset `size` max-width when not `fullscreen`. Merged into `style` so it wins over CSS classes. */
    maxWidth?: CSSProperties['maxWidth']
    closeOnPointerDownOutside?: boolean
    onEscapeKeyDown?: ComponentProps<typeof DialogPrimitive.Content>['onEscapeKeyDown']
  }

const MotionArticle = motion.article

const ANIMATE_TRANSITION: Transition = {
  ...framerTransitions.easeInOutCubic,
  duration: framerTransitions.defaultDuration,
}

function getCssLength(value: CSSProperties['maxWidth']) {
  return typeof value === 'number' ? `${value}px` : value
}

function Content({
  children,
  variant = 'default',
  size = 'lg',
  mobileLayout = 'dialog',
  maxWidth,
  closeOnPointerDownOutside = true,
  onEscapeKeyDown,
  className,
  style,
  ...motionArticleProps
}: ContentProps) {
  const isClosing = useContext(Dialog.IsClosingContext)

  const handleInteractOutside = (
    event: Parameters<NonNullable<ComponentProps<typeof DialogPrimitive.Content>['onInteractOutside']>>[0],
  ) => {
    if (event.type !== 'dismissableLayer.pointerDownOutside' || !closeOnPointerDownOutside) {
      event.preventDefault()
    }
  }

  const contentStyle =
    size !== 'fullscreen' && maxWidth !== undefined
      ? { ...assignInlineVars({ [contentMaxWidthVar]: getCssLength(maxWidth) }), ...style }
      : style

  return (
    <AnimatePresence>
      {!isClosing && (
        <DialogPrimitive.Content onEscapeKeyDown={onEscapeKeyDown} onInteractOutside={handleInteractOutside} asChild>
          <MotionArticle
            className={clsx(content({ variant, size, mobileLayout }), contentPaddingStyle, className)}
            style={contentStyle}
            initial={{ opacity: 0, y: '2vh' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '2vh' }}
            transition={ANIMATE_TRANSITION}
            {...motionArticleProps}
          >
            {children}
          </MotionArticle>
        </DialogPrimitive.Content>
      )}
    </AnimatePresence>
  )
}

type CloseButtonProps = ComponentProps<typeof IconButton>

function CloseButton({ className, ...props }: CloseButtonProps) {
  return (
    <Dialog.Close asChild>
      <IconButton
        variant="secondary"
        Icon={<CrossIcon size="1.5rem" />}
        className={clsx(closeButton, className)}
        {...props}
      />
    </Dialog.Close>
  )
}

type TitleProps = ComponentProps<typeof Typography>

function Title({ children, className, ...props }: TitleProps) {
  const RootElement = props.asChild ? Slot : 'h2'

  return (
    <DialogPrimitive.Title asChild>
      <Typography variant="standard.18" className={className} asChild {...props}>
        <RootElement>{children}</RootElement>
      </Typography>
    </DialogPrimitive.Title>
  )
}

type DescriptionProps = ComponentProps<typeof Typography>

function Description({ children, className, ...props }: DescriptionProps) {
  return (
    <DialogPrimitive.Description asChild>
      <Typography
        variant="standard.18"
        className={className}
        color={tokens.colors.text.translucent.secondary}
        {...props}
      >
        {children}
      </Typography>
    </DialogPrimitive.Description>
  )
}

type HeaderProps = ComponentProps<'header'>

function Header({ children, className, ...props }: HeaderProps) {
  return (
    <header
      className={clsx(xStack({ gap: 'sm', alignItems: 'center', justifyContent: 'space-between' }), className)}
      {...props}
    >
      {children}
    </header>
  )
}

type BodyProps = ComponentProps<'div'> & { scrollable?: boolean }

function Body({ children, scrollable = false, className, ...props }: BodyProps) {
  return (
    <div className={clsx(body, scrollable && bodyScrollable, className)} {...props}>
      {children}
    </div>
  )
}

type FooterProps = ComponentProps<'footer'>

function Footer({ children, className, ...props }: FooterProps) {
  return (
    <footer className={clsx(xStack({ gap: 'sm' }), className)} {...props}>
      {children}
    </footer>
  )
}

export const BaseDialog = {
  Root,
  Trigger: Dialog.Trigger,
  Close: Dialog.Close,
  Title,
  Description,
  Overlay,
  Content,
  CloseButton,
  Header,
  Body,
  Footer,
}
